import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { X, CheckCircle2, AlertTriangle, Info, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  ToastContext,
  type ToastContextValue,
  type ToastInput,
  type ToastKind,
} from './toast-context'

interface ToastItem extends Required<Omit<ToastInput, 'action' | 'title' | 'description'>> {
  title?: string
  description?: string
  action?: ToastInput['action']
}

const DEFAULT_DURATION = 3000

function createId(): string {
  return `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const timersRef = useRef<Map<string, number>>(new Map())

  const dismiss = useCallback((id: string) => {
    const timer = timersRef.current.get(id)
    if (timer) {
      window.clearTimeout(timer)
      timersRef.current.delete(id)
    }
    setItems((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const show = useCallback<ToastContextValue['show']>(
    (t: ToastInput) => {
      const id = t.id ?? createId()
      const item: ToastItem = {
        id,
        kind: t.kind ?? 'info',
        duration: t.duration ?? DEFAULT_DURATION,
        title: t.title,
        description: t.description,
        action: t.action,
      }
      setItems((prev) => [...prev.filter((x) => x.id !== id), item])
      if (item.duration > 0) {
        const timer = window.setTimeout(() => dismiss(id), item.duration)
        timersRef.current.set(id, timer)
      }
      return id
    },
    [dismiss],
  )

  const value = useMemo(() => ({ show, dismiss }), [show, dismiss])

  useEffect(() => {
    const timers = timersRef.current
    return () => {
      for (const timer of timers.values()) window.clearTimeout(timer)
      timers.clear()
    }
  }, [])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport items={items} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

function ToastViewport({
  items,
  onDismiss,
}: {
  items: ToastItem[]
  onDismiss: (id: string) => void
}) {
  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-4 z-[80] flex flex-col items-center gap-2 px-4 sm:right-4 sm:bottom-4 sm:left-auto sm:items-end"
      role="region"
      aria-label="Notificaciones"
    >
      {items.map((t) => (
        <ToastRow key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  )
}

const kindStyles: Record<ToastKind, { border: string; icon: ReactNode }> = {
  success: {
    border: 'border-l-[color:var(--color-fitlab-green)]',
    icon: <CheckCircle2 className="h-5 w-5 text-[color:var(--color-fitlab-green)]" aria-hidden />,
  },
  error: {
    border: 'border-l-[color:var(--color-fitlab-red)]',
    icon: <AlertCircle className="h-5 w-5 text-[color:var(--color-fitlab-red)]" aria-hidden />,
  },
  info: {
    border: 'border-l-[color:var(--color-fitlab-blue)]',
    icon: <Info className="h-5 w-5 text-[color:var(--color-fitlab-blue)]" aria-hidden />,
  },
  warning: {
    border: 'border-l-amber-500',
    icon: <AlertTriangle className="h-5 w-5 text-amber-600" aria-hidden />,
  },
}

function ToastRow({ toast, onDismiss }: { toast: ToastItem; onDismiss: () => void }) {
  const { border, icon } = kindStyles[toast.kind]
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-md border border-slate-200 border-l-4 bg-white p-3 shadow-lg',
        border,
      )}
      data-toast-kind={toast.kind}
    >
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div className="min-w-0 flex-1">
        {toast.title && <p className="text-sm font-semibold text-slate-900">{toast.title}</p>}
        {toast.description && (
          <p className="mt-0.5 text-sm text-slate-600 break-words">{toast.description}</p>
        )}
        {toast.action && (
          <button
            type="button"
            onClick={() => {
              toast.action?.onClick()
              onDismiss()
            }}
            className="mt-2 text-xs font-semibold text-[color:var(--color-fitlab-blue)] hover:underline"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Cerrar notificación"
        className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
